import type { Component } from '@ydegouvello/ui';
import type { IPowerbiPageDto } from 'dtos/powerbi-page.dto';
import type { IPowerbiReportDto } from 'dtos/powerbi-report.dto';
import type { IPowerbiTokenDto } from 'dtos/powerbi-token.dto';
import type { IPowerbiWorkspaceDto } from 'dtos/powerbi-workspace.dto';
import { tokenize } from 'helpers/string.helper';
import * as pbi from 'powerbi-client';
import { ReportsRepository } from 'repositories/reports.repository';
import { SessionsRepository } from 'repositories/sessions.repository';
import { VisitRepository } from 'repositories/visit.repository';
import { AbstractComponent } from './abstract.component';

// Important line to ensure that PowerBI lib is effectively loaded
if (pbi.Embed.name) console.log('Power BI client library loaded');

export class ReportComponent extends AbstractComponent {
  private static _instance: ReportComponent | undefined = undefined;
  private _token: IPowerbiTokenDto | undefined = undefined;
  private _report: pbi.Report | undefined = undefined;
  private _reportLoading: boolean = false;
  private readonly _currentDetails: {
    workspace?: IPowerbiWorkspaceDto;
    report?: IPowerbiReportDto;
    page?: IPowerbiPageDto;
  } = { workspace: undefined, report: undefined, page: undefined };

  private constructor(
    private readonly _current: { workspace?: string; report?: string; page?: string },
    name: string,
    parent?: { readonly containerHtmlElementSelector: string; readonly component: Component },
    readonly reportRepository: ReportsRepository = ReportsRepository.getInstance(),
    readonly sessionRepository: SessionsRepository = SessionsRepository.getInstance(),
    readonly visitRepository: VisitRepository = VisitRepository.getInstance(),
  ) {
    super(name, parent);
  }

  static getInstance(
    current?: { workspace?: string; report?: string; page?: string },
    name = 'report',
    parent?: { readonly containerHtmlElementSelector: string; readonly component: Component },
    reportRepository: ReportsRepository = ReportsRepository.getInstance(),
    sessionRepository: SessionsRepository = SessionsRepository.getInstance(),
  ): ReportComponent {
    this._instance ??= new this(current ?? {}, name, parent, reportRepository, sessionRepository);
    this._instance._current.workspace = current?.workspace;
    this._instance._current.report = current?.report;
    this._instance._current.page = current?.page;
    return this._instance;
  }

  protected override async render(): Promise<void> {
    await this.setLoading(true);

    this.getById('report-menu-toggle').style.display = 'none';
    this.getById('report-menu').style.display = 'none';
    this.getById('report-container').style.display = 'none';

    try {
      // GETTING SESSION & TOKEN DETAILS FROM BACKEND
      const session = await this.sessionRepository.get();
      if (!session) throw new Error('Session not found');
      if (!session.active) throw new Error('Session is not active');

      this._token = await this.reportRepository.getTokenDetails(session.id);
      if (!this._token) throw new Error('Token not found');

      // RENDERING REPORT
      this._currentDetails.workspace =
        this._token.workspaces.find((workspace) => tokenize(workspace.name) === this._current.workspace) ??
        this._token.workspaces[0];
      this._currentDetails.report =
        this._currentDetails.workspace.reports.find((report) => tokenize(report.name) === this._current.report) ??
        this._currentDetails.workspace.reports[0];

      this._reportLoading = true;
      await this.renderReport();

      // SETTING CURRENT PAGE
      this._currentDetails.report.pages = await this.getReportPages();
      this._currentDetails.page = this._current.page
        ? this._currentDetails.report.pages.find((page) => tokenize(page.displayName) === this._current.page)
        : undefined;
      this._currentDetails.page ??=
        this._currentDetails.report.pages.find((page) => page.isActive) ?? this._currentDetails.report.pages[0];

      await new Promise<void>((resolve) => this.setTimeout(() => void resolve(), 1000));
      await this._currentDetails.page.setActive();
      this._reportLoading = false;

      // RENDERING MENU
      this.renderMenu();

      // DISPLAYING REPORT & MENU
      this.getById('report-menu-toggle').style.removeProperty('display');
      this.getById('report-container').style.removeProperty('display');

      // LOG PAGE VIEW
      await this.logCurrentPage();
    } finally {
      await this.setLoading(false);
    }
  }

  private renderReport(): Promise<void> {
    return new Promise((resolve) => {
      const embedContainer = this.getById('report-container');
      const reportConfig = {
        type: 'report',
        tokenType: 1,
        accessToken: this._token?.token,
        embedUrl: this._currentDetails.report?.embedUrl,
        id: this._currentDetails.report?.id,
        settings: { panes: { pageNavigation: { visible: true, position: 0 } } },
      };

      if (this._report) window.powerbi.reset(embedContainer);

      this._report = window.powerbi.embed(embedContainer, reportConfig) as pbi.Report;
      this._report.on('pageChanged', (event: { detail: { newPage: IPowerbiPageDto | undefined } }) => {
        if (this._reportLoading) return;
        const newPage = event.detail.newPage;
        if (newPage) {
          const newHash = this.getHash({
            workspace: this._currentDetails.workspace,
            report: this._currentDetails.report,
            page: newPage,
          });
          if (this.navigation.location !== newHash) {
            void (async () => {
              await this.navigation.push(newHash);
              this._current.page = tokenize(newPage.displayName);
              this._currentDetails.page = newPage;
              const prevMenuEntry = document.querySelector('.report-menu-page.current');
              if (prevMenuEntry) prevMenuEntry.classList.remove('current');
              const newMenuEntry = document.getElementById(`page-${tokenize(newPage.displayName)}`);
              if (newMenuEntry) newMenuEntry.classList.add('current');

              // LOG PAGE VIEW
              await this.logCurrentPage();
            })();
          }
        }
      });

      this._report.on('loaded', () => resolve());
    });
  }

  private async logCurrentPage(): Promise<void> {
    const w = this._currentDetails.workspace?.name ?? '';
    const r = this._currentDetails.report?.name ?? '';
    const t = this._currentDetails.page?.displayName ?? '';

    await this.visitRepository.log({ w, r, t });
  }

  private renderMenu(): void {
    this.htmlElement?.querySelector('#report-menu-entries')?.remove();

    const reportsMenu = document.createElement('ul');
    reportsMenu.id = 'report-menu-entries';
    this._token?.workspaces.forEach((workspace) => {
      const workspaceItem = document.createElement('li');
      workspaceItem.id = `workspace-${tokenize(workspace.name)}`;
      workspaceItem.className = 'report-menu-workspace';
      const hash = this.getHash({ workspace });

      const workspaceLink = document.createElement('a');
      workspaceLink.innerText = workspace.name;
      workspaceLink.href = `#${hash}`;
      workspaceLink.addEventListener(
        'click',
        (event) =>
          void (async () => {
            event.preventDefault();
            await this.toggleMenuOpen();
            await this.goto({ workspace });
          })(),
      );

      workspaceItem.appendChild(workspaceLink);

      const reportsList = document.createElement('ul');
      workspace.reports.forEach((report) => {
        const reportItem = document.createElement('li');
        reportItem.id = `report-${tokenize(report.name)}`;
        reportItem.className = 'report-menu-report';
        const hash = this.getHash({ workspace, report });

        const reportLink = document.createElement('a');
        reportLink.innerText = report.name;
        reportLink.href = `#${hash}`;
        reportLink.addEventListener(
          'click',
          (event) =>
            void (async () => {
              event.preventDefault();
              await this.toggleMenuOpen();
              await this.goto({ workspace, report });
            })(),
        );

        reportItem.appendChild(reportLink);

        if (this._currentDetails.report?.id === report.id) {
          const pagesList = document.createElement('ul');
          report.pages.forEach((page) => {
            const pageItem = document.createElement('li');
            pageItem.id = `page-${tokenize(page.displayName)}`;
            pageItem.className = 'report-menu-page';
            const hash = this.getHash({ workspace, report, page });
            if (hash === this.getHash(this._currentDetails)) {
              pageItem.classList.add('current');
            }

            const pageLink = document.createElement('a');
            pageLink.innerText = page.displayName;
            pageLink.href = `#${hash}`;
            pageLink.addEventListener(
              'click',
              (event) =>
                void (async () => {
                  event.preventDefault();
                  await this.toggleMenuOpen();
                  await this.goto({ workspace, report, page });
                })(),
            );

            pageItem.appendChild(pageLink);

            pagesList.appendChild(pageItem);
          });
          reportItem.appendChild(pagesList);
        }
        reportsList.appendChild(reportItem);
      });
      workspaceItem.appendChild(reportsList);
      reportsMenu.appendChild(workspaceItem);
    });
    this.getById('report-menu').prepend(reportsMenu);

    this.getById('report-menu-toggle').setAttribute('class', 'closed');
    this.addEventListener({
      element: this.getById('report-menu-toggle'),
      eventName: 'click',
      handler: () => void this.toggleMenu(),
    });
    this.addEventListener({
      element: window.document.getRootNode() as HTMLElement,
      eventName: 'click',
      handler: (e: MouseEvent) => void this.closeMenuIfOpen(e),
    });

    this.setLogoutHandler();
    this.setFullscreenHandler();
  }

  private async toggleMenu(): Promise<void> {
    const transitionState = this.getById('report-menu-toggle').getAttribute('class');
    if (transitionState === 'open') {
      await this.toggleMenuOpen();
    } else if (transitionState === 'closed') {
      await this.toggleMenuClosed();
    }
  }

  private async toggleMenuOpen(): Promise<void> {
    const transitionState = this.getById('report-menu-toggle').getAttribute('class');
    if (transitionState !== 'open') return;
    this.getById('report-menu-toggle').setAttribute('class', 'closing');
    await animateTopToBottom(this.getById('report-menu'), false);
    this.getById('report-menu-toggle').setAttribute('class', 'closed');
  }

  private async toggleMenuClosed(): Promise<void> {
    const transitionState = this.getById('report-menu-toggle').getAttribute('class');
    if (transitionState !== 'closed') return;
    this.getById('report-menu-toggle').setAttribute('class', 'opening');
    await animateTopToBottom(this.getById('report-menu'), true);
    this.getById('report-menu-toggle').setAttribute('class', 'open');
  }

  private async closeMenuIfOpen(e: MouseEvent): Promise<void> {
    const reportMenu = this.getById('report-menu');
    const reportMenuToggle = this.getById('report-menu-toggle');

    const target = e.target as HTMLElement | null;
    if (target && (reportMenu.contains(target) || reportMenuToggle.contains(target))) {
      return;
    }
    void this.toggleMenuOpen();
  }

  private setLogoutHandler() {
    this.getById('logout-button').addEventListener(
      'click',
      (event) =>
        void (async () => {
          event.preventDefault();

          await this.safeCall(async () => {
            await this.setLoading(true);

            try {
              await this.sessionRepository.logout();
              await this.navigation.refresh();
            } finally {
              await this.setLoading(false);
            }
          });
        })(),
    );
  }

  private setFullscreenHandler() {
    this.getById('fullscreen-button').addEventListener(
      'click',
      (event) =>
        void (async () => {
          event.preventDefault();

          const elem = this.htmlElement as HTMLElement;
          if (!document.fullscreenElement) {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (elem.requestFullscreen) {
              await elem.requestFullscreen();
            }
          } else {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (document.exitFullscreen) {
              await document.exitFullscreen();
            }
          }
        })(),
    );
  }

  private async goto(details: {
    workspace: IPowerbiWorkspaceDto;
    report?: IPowerbiReportDto;
    page?: IPowerbiPageDto;
  }): Promise<void> {
    if (
      details.workspace.id !== this._currentDetails.workspace?.id ||
      (details.report?.id ?? this._currentDetails.workspace.reports[0]?.id) !== this._currentDetails.report?.id
    ) {
      await this.navigation.push(this.getHash(details));
      this._current.workspace = tokenize(details.workspace.name);
      this._current.report = details.report ? tokenize(details.report.name) : undefined;
      this._current.page = details.page ? tokenize(details.page.displayName) : undefined;
      return await this.render();
    }
    if (!details.page || details.page.name === this._currentDetails.page?.name) return;
    await details.page.setActive();
  }

  private async getReportPages(): Promise<IPowerbiPageDto[]> {
    if (!this._report) return await Promise.resolve([]);
    const pages = await this._report.getPages();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    return pages.filter((page) => page.visibility === 0);
  }

  private getHash({
    workspace,
    report,
    page,
  }: {
    workspace?: IPowerbiWorkspaceDto | undefined;
    report?: IPowerbiReportDto | undefined;
    page?: IPowerbiPageDto | undefined;
  }): string {
    if (!workspace) return '/';
    if (!report) return `/${tokenize(workspace.name)}`;
    if (!page) return `/${tokenize(workspace.name)}/${tokenize(report.name)}`;
    return `/${tokenize(workspace.name)}/${tokenize(report.name)}/${tokenize(page.displayName)}`;
  }
}

function animateTopToBottom(element: HTMLElement, show: boolean, durationMs = 300) {
  return new Promise<void>((resolve) => {
    element.style.overflow = 'hidden';
    element.style.transition = `height ${durationMs}ms cubic-bezier(0.4,0,0.2,1)`;
    if (show) {
      element.style.display = '';
      element.style.height = '0px';
      // Force reflow
      void element.offsetHeight;
      const targetHeight = element.scrollHeight + 'px';
      element.style.height = targetHeight;
      const onTransitionEnd = () => {
        element.style.height = '';
        element.style.overflow = '';
        element.removeEventListener('transitionend', onTransitionEnd);
        resolve();
      };
      element.addEventListener('transitionend', onTransitionEnd);
    } else {
      element.style.height = element.offsetHeight + 'px';
      // Force reflow
      void element.offsetHeight;
      element.style.height = '0px';
      const onTransitionEnd = () => {
        element.style.display = 'none';
        element.style.height = '';
        element.style.overflow = '';
        element.removeEventListener('transitionend', onTransitionEnd);
        resolve();
      };
      element.addEventListener('transitionend', onTransitionEnd);
    }
  });
}
